import * as THREE from 'three';
import { PRIORITY } from '../core/Engine.js';
import { DEG } from '../util/math.js';

/**
 * Sun + sky + image-based lighting.
 *
 * The sky is a shader dome (Rayleigh/Mie approximation) that is also baked to a
 * PMREM cube once at init and used as the scene environment, so metals reflect
 * the actual sky rather than a flat ambient term. Re-bake only if the sun moves.
 */
const SKY_VERT = /* glsl */`
  varying vec3 vWorldDir;
  void main() {
    vec4 wp = modelMatrix * vec4(position, 1.0);
    vWorldDir = normalize(wp.xyz - cameraPosition);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    gl_Position.z = gl_Position.w; // force to far plane
  }
`;

const SKY_FRAG = /* glsl */`
  varying vec3 vWorldDir;
  uniform vec3 uSunDir;
  uniform vec3 uZenith;
  uniform vec3 uHorizon;
  uniform vec3 uGround;
  uniform vec3 uSunColor;
  uniform float uTurbidity;
  uniform float uHaze;

  void main() {
    vec3 d = normalize(vWorldDir);
    float h = d.y;

    // Sky gradient: zenith -> horizon, with a soft ground bounce below.
    float t = clamp(h, 0.0, 1.0);
    vec3 sky = mix(uHorizon, uZenith, pow(t, 0.42));
    vec3 col = mix(uGround, sky, smoothstep(-0.12, 0.06, h));

    // Mie forward scattering: broad glow that hugs the sun, strongest near horizon.
    float mu = max(dot(d, uSunDir), 0.0);
    float mie = pow(mu, 8.0) * 0.55 + pow(mu, 2.0) * 0.12;
    float horizonBoost = 1.0 - smoothstep(0.0, 0.45, abs(h));
    col += uSunColor * mie * (0.6 + horizonBoost * uTurbidity);

    // Dust haze near the horizon — sells the arid setting and hides the seam
    // where geometry meets the dome.
    col = mix(col, uHorizon * 1.08, horizonBoost * uHaze);

    // Sun disc with a soft limb so bloom has something bright to grab.
    float disc = smoothstep(0.99965, 0.99991, mu);
    col += uSunColor * disc * 40.0;

    gl_FragColor = vec4(col, 1.0);
  }
`;

/**
 * Padrão árido — exatamente o que o Blackout usava quando isto eram constantes
 * fixas aqui dentro. Um mapa que não declare `ambiente` continua ganhando este
 * céu, e um mapa que declare só metade herda a outra metade daqui.
 */
const PADRAO = {
  sol: { azimute: 118, elevacao: 48, intensidade: 11.0, cor: 0xfff2da },
  ceu: { zenite: 0x4a86c8, horizonte: 0xcfd0c4, chao: 0x6d6250, corSol: 0xfff4e0, turbidez: 1.2, neblina: 0.34 },
  hemi: { ceu: 0x8fb2d8, chao: 0x7d6b4e, intensidade: 2.6 },
  fog: { cor: 0xc6b899, densidade: 0.0034 },
};

const lin = (hex) => new THREE.Color(hex).convertSRGBToLinear();

export class Lighting {
  // 48 graus é meio da manhã: alto o bastante para ler como luz do dia e pôr
  // luz nas superfícies horizontais, baixo o bastante para as paredes ainda
  // jogarem sombra longa e direcional. Nos 17 graus anteriores o sol raspava
  // tudo e nenhum ajuste de exposição salvava a imagem.
  constructor({ azimuth = null, elevation = null, seed = 1 } = {}) {
    this.name = 'lighting';
    this.priority = PRIORITY.WORLD;
    // Vindos por parâmetro, mandam mais que o mapa (é como um teste força o sol).
    this._azOverride = azimuth;
    this._elOverride = elevation;
    this.azimuth = azimuth ?? PADRAO.sol.azimute;
    this.elevation = elevation ?? PADRAO.sol.elevacao;
    this.sunDir = new THREE.Vector3();
    this._pmrem = null;
  }

  /**
   * O ambiente sai do mapa. O Level é registrado antes do Lighting em
   * `main.js` e o motor inicializa na ordem de registro, então `get('level')`
   * já responde aqui. Se um dia não responder, cai no padrão — sem sol o jogo
   * fica preto, e isso não pode depender de ordem de arquivo.
   */
  _ambiente(engine) {
    const a = engine.get('level')?.ambiente || {};
    return {
      sol: { ...PADRAO.sol, ...(a.sol || {}) },
      ceu: { ...PADRAO.ceu, ...(a.ceu || {}) },
      hemi: { ...PADRAO.hemi, ...(a.hemi || {}) },
      fog: a.fog === null ? null : { ...PADRAO.fog, ...(a.fog || {}) },
    };
  }

  init(engine) {
    const { scene, renderer } = engine;
    this.engine = engine;

    const amb = this._ambiente(engine);
    this.ambiente = amb;
    this.azimuth = this._azOverride ?? amb.sol.azimute;
    this.elevation = this._elOverride ?? amb.sol.elevacao;

    this._computeSunDir();

    // --- sky dome -----------------------------------------------------------
    this.skyUniforms = {
      uSunDir: { value: this.sunDir.clone() },
      // A paleta do céu é casada com a elevação do sol declarada pelo mapa. O
      // par zênite/horizonte anterior era de fim de tarde: com o sol alto lia
      // como crepúsculo roxo sobre um pátio iluminado, que é a pista mais óbvia
      // de que céu e iluminação foram feitos em horas do dia diferentes.
      uZenith: { value: lin(amb.ceu.zenite) },
      uHorizon: { value: lin(amb.ceu.horizonte) },
      uGround: { value: lin(amb.ceu.chao) },
      uSunColor: { value: lin(amb.ceu.corSol) },
      uTurbidity: { value: amb.ceu.turbidez },
      uHaze: { value: amb.ceu.neblina },
    };
    const skyMat = new THREE.ShaderMaterial({
      vertexShader: SKY_VERT, fragmentShader: SKY_FRAG,
      uniforms: this.skyUniforms,
      side: THREE.BackSide, depthWrite: false, depthTest: true, fog: false,
    });
    this.sky = new THREE.Mesh(new THREE.SphereGeometry(1, 32, 16), skyMat);
    this.sky.frustumCulled = false;
    this.sky.renderOrder = -1000;
    this.sky.scale.setScalar(400);
    scene.add(this.sky);

    // --- sun ----------------------------------------------------------------
    const q = engine.quality;
    const shadowSize = q === 'ultra' ? 4096 : q === 'high' ? 2048 : 1024;

    this.sun = new THREE.DirectionalLight(amb.sol.cor, amb.sol.intensidade);
    this.sun.position.copy(this.sunDir).multiplyScalar(120);
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(shadowSize, shadowSize);
    const cam = this.sun.shadow.camera;
    cam.near = 1; cam.far = 260;
    cam.left = -55; cam.right = 55; cam.top = 55; cam.bottom = -55;
    // VSM needs a real radius to look soft; bias tuned against the 1u wall thickness.
    this.sun.shadow.radius = q === 'low' ? 2 : 4;
    this.sun.shadow.blurSamples = q === 'ultra' ? 16 : 8;
    this.sun.shadow.bias = -0.0006;
    this.sun.shadow.normalBias = 0.035;
    scene.add(this.sun);
    scene.add(this.sun.target);

    // --- fill -----------------------------------------------------------
    // Sky fill matters more than it looks. Outdoors, shadowed ground sits about
    // an eighth as bright as lit ground, not a hundredth — without a real fill
    // term every shadow becomes a black hole and half the frame clips.
    this.hemi = new THREE.HemisphereLight(amb.hemi.ceu, amb.hemi.chao, amb.hemi.intensidade);
    scene.add(this.hemi);

    // --- fog ------------------------------------------------------------
    // Light enough to give aerial perspective across the 70 m arena without
    // washing out distant contrast; the previous density milked the far field.
    scene.fog = amb.fog ? new THREE.FogExp2(lin(amb.fog.cor), amb.fog.densidade) : null;

    this._bakeEnvironment(renderer, scene);
    engine.provide('lighting', this);
  }

  _computeSunDir() {
    const az = this.azimuth * DEG, el = this.elevation * DEG;
    this.sunDir.set(
      Math.cos(el) * Math.sin(az),
      Math.sin(el),
      Math.cos(el) * Math.cos(az),
    ).normalize();
  }

  /** Render the sky dome into a PMREM cube so PBR metals get real reflections. */
  _bakeEnvironment(renderer, scene) {
    const pmrem = new THREE.PMREMGenerator(renderer);
    pmrem.compileEquirectangularShader();
    const skyScene = new THREE.Scene();
    const clone = this.sky.clone();
    clone.material = this.sky.material;
    skyScene.add(clone);
    const rt = pmrem.fromScene(skyScene, 0.04, 0.1, 1000);
    scene.environment = rt.texture;
    scene.environmentIntensity = 1.0;
    this._pmrem = pmrem;
    this._envRT = rt;
  }

  /**
   * Keep the shadow frustum centered on the player. A static frustum either
   * wastes texels on geometry behind you or clips shadows in front of you.
   */
  update(dt, alpha, engine) {
    const p = engine.camera.position;
    this.sun.target.position.set(p.x, 0, p.z);
    this.sun.position.set(p.x, 0, p.z).addScaledVector(this.sunDir, 120);
    this.sun.target.updateMatrixWorld();
    this.sky.position.copy(p);
  }

  dispose() {
    this._envRT?.dispose();
    this._pmrem?.dispose();
    this.sky.geometry.dispose();
    this.sky.material.dispose();
  }
}
